import type { ContactFormSelectOptionT } from "./contact-form-types";
import { normalizeLocationIds } from "@/app/services/server/frontend/location-scope-filter";

export type ContactFormLocationSourceT = {
  readonly id: string;
  readonly name?: string | null;
  readonly location_name?: string | null;
  readonly city?: string | null;
  readonly zip_code?: string | null;
  readonly address?: string | null;
  readonly company_name?: string | null;
};

export type ContactFormLocationSelectionModeT =
  | "unavailable"
  | "locked"
  | "select";

export type ContactFormLocationSelectionT = {
  readonly options: ContactFormSelectOptionT[];
  readonly lockedOption?: ContactFormSelectOptionT;
  readonly lockedSelectValues?: {
    readonly location: string;
  };
  readonly mode: ContactFormLocationSelectionModeT;
};

type ContactFormLocationMatchInputT = {
  readonly city?: string | null;
  readonly zipCode?: string | null;
  readonly address?: string | null;
  readonly companyName?: string | null;
  readonly locationName?: string | null;
};

const normalizeText = (value?: string | null): string =>
  value?.trim().toLowerCase().replace(/\s+/g, " ") ?? "";

const hasExactTextMatch = (
  location: ContactFormLocationSourceT,
  ...targets: Array<string | null | undefined>
): boolean => {
  const normalizedTargets = targets.map(normalizeText).filter(Boolean);

  if (normalizedTargets.length === 0) {
    return false;
  }

  const normalizedCandidates = [
    location.location_name,
    location.name,
    location.company_name,
  ]
    .map(normalizeText)
    .filter(Boolean);

  return normalizedTargets.some((target) =>
    normalizedCandidates.includes(target)
  );
};

export const getContactFormLocationLabel = (
  location: ContactFormLocationSourceT
): string | null => {
  const label =
    location.location_name?.trim() ||
    location.name?.trim() ||
    location.city?.trim();

  return label && label.length > 0 ? label : null;
};

export const filterContactFormLocationsByIds = <
  TLocation extends ContactFormLocationSourceT,
>(
  locations: readonly TLocation[],
  allowedLocationIds?: readonly string[]
): TLocation[] => {
  const normalizedAllowedLocationIds = normalizeLocationIds(
    allowedLocationIds ? [...allowedLocationIds] : undefined
  );

  if (normalizedAllowedLocationIds.length === 0) {
    return [...locations];
  }

  const allowedLocationIdSet = new Set(normalizedAllowedLocationIds);

  return locations.filter((location) => allowedLocationIdSet.has(location.id));
};

export const mapLocationsToContactFormSelectOptions = <
  TLocation extends ContactFormLocationSourceT,
>(
  locations: readonly TLocation[]
): ContactFormSelectOptionT[] => {
  const options = new Map<string, ContactFormSelectOptionT>();

  locations.forEach((location) => {
    const label = getContactFormLocationLabel(location);

    if (!label) {
      return;
    }

    options.set(location.id, {
      value: location.id,
      label,
    });
  });

  return Array.from(options.values()).sort((a, b) =>
    a.label.localeCompare(b.label, "de")
  );
};

export const resolveLockedContactFormLocationOption = (
  options: readonly ContactFormSelectOptionT[]
): ContactFormSelectOptionT | undefined =>
  options.length === 1 ? options[0] : undefined;

export const resolveContactFormLocationSelection = <
  TLocation extends ContactFormLocationSourceT,
>(
  locations: readonly TLocation[]
): ContactFormLocationSelectionT => {
  const options = mapLocationsToContactFormSelectOptions(locations);
  const lockedOption = resolveLockedContactFormLocationOption(options);

  if (options.length === 0) {
    return {
      options,
      mode: "unavailable",
    };
  }

  if (lockedOption) {
    return {
      options: [lockedOption],
      lockedOption,
      lockedSelectValues: {
        location: lockedOption.value,
      },
      mode: "locked",
    };
  }

  return {
    options,
    mode: "select",
  };
};

export const resolveBestMatchingContactFormLocation = <
  TLocation extends ContactFormLocationSourceT,
>(
  locations: readonly TLocation[],
  input: ContactFormLocationMatchInputT
): TLocation | null => {
  if (locations.length === 0) {
    return null;
  }

  const normalizedCity = normalizeText(input.city);
  const normalizedZipCode = normalizeText(input.zipCode);
  const normalizedAddress = normalizeText(input.address);

  const addressZipCityMatches = locations.filter((location) => {
    if (!normalizedCity || !normalizedZipCode || !normalizedAddress) {
      return false;
    }

    return (
      normalizeText(location.city) === normalizedCity &&
      normalizeText(location.zip_code) === normalizedZipCode &&
      normalizeText(location.address) === normalizedAddress
    );
  });

  if (addressZipCityMatches.length === 1) {
    return addressZipCityMatches[0] ?? null;
  }

  const zipCityMatches = locations.filter((location) => {
    if (!normalizedCity || !normalizedZipCode) {
      return false;
    }

    return (
      normalizeText(location.city) === normalizedCity &&
      normalizeText(location.zip_code) === normalizedZipCode
    );
  });

  if (zipCityMatches.length === 1) {
    return zipCityMatches[0] ?? null;
  }

  const nameMatches = locations.filter((location) =>
    hasExactTextMatch(location, input.locationName, input.companyName)
  );

  if (nameMatches.length === 1) {
    return nameMatches[0] ?? null;
  }

  const cityMatches = locations.filter((location) => {
    if (!normalizedCity) {
      return false;
    }

    return normalizeText(location.city) === normalizedCity;
  });

  if (cityMatches.length === 1) {
    return cityMatches[0] ?? null;
  }

  return null;
};
